/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * localTimerModel.js
 *
 * Pure state transitions of the DEVICE-LOCAL stopwatches (`TimeTrackingContext`:
 * the dashboard's quick "coffee / lunch / travel / custom" activities, which have
 * no work order and therefore no backend clock).
 *
 * The old model counted `+1000` per `setInterval` tick. Android suspends JS timers
 * while the phone is locked, so every locked minute vanished. This model never
 * counts ticks. A timer keeps
 *
 *   - `totalElapsed` / `breakElapsed`  BANKED milliseconds of segments that are over, and
 *   - `segments[]`                     of which at most the last one is still open
 *                                      (`start` set, no `end`),
 *
 * and the live value is always `banked + (now - open.start)` ({@link withLiveElapsed}).
 * The only thing read from the device is the wall clock, which keeps running while
 * locked and survives an app restart, so there is nothing to lose.
 *
 * Work-order timers do NOT live here any more — the backend owns those clocks
 * (see `helpers/useWorkOrderTimers`).
 *
 * @module helpers/localTimerModel
 */

/** Persisted-shape version; timers without it were written by the tick-counting model. */
export const LOCAL_TIMER_VERSION = 2;

/**
 * The still-open segment of a timer, if any.
 * @param {object} timer Local timer.
 * @returns {{type: string, start: number}|null} The open segment.
 */
const openSegmentOf = (timer) => {
    const segments = timer?.segments || [];
    const last = segments[segments.length - 1];

    return last && !last.end && Number.isFinite(Number(last.start)) ? last : null;
};

/**
 * Close the open segment at `now` and bank its length into the matching counter
 * (work → `totalElapsed`, break → `breakElapsed`). A timer with no open segment is
 * returned unchanged.
 *
 * @param {object} timer Local timer.
 * @param {number} now Epoch ms.
 * @returns {object} New timer (never mutates the input).
 */
export const closeOpenSegment = (timer, now) => {
    const open = openSegmentOf(timer);

    if (!open) {
        return timer;
    }

    const segments = [...timer.segments];
    const length = Math.max(0, now - Number(open.start));

    segments[segments.length - 1] = {...open, end: now};

    return open.type === 'break'
        ? {...timer, segments, breakElapsed: (timer.breakElapsed || 0) + length}
        : {...timer, segments, totalElapsed: (timer.totalElapsed || 0) + length};
};

/**
 * Display value of a timer at `now`: banked time + the open segment's length.
 * Paused / idle timers have no open segment and are returned as stored.
 *
 * @param {object} timer Local timer.
 * @param {number} [now] Epoch ms (injectable for tests).
 * @returns {object} Timer whose `totalElapsed` / `breakElapsed` are live values.
 */
export const withLiveElapsed = (timer, now = Date.now()) => {
    if (!timer || (timer.status !== 'running' && timer.status !== 'break')) {
        return timer;
    }

    const open = openSegmentOf(timer);

    if (!open) {
        return timer;
    }

    const live = Math.max(0, now - Number(open.start));

    return timer.status === 'running'
        ? {...timer, totalElapsed: (timer.totalElapsed || 0) + live}
        : {...timer, breakElapsed: (timer.breakElapsed || 0) + live};
};

/**
 * Start (or restart) a timer. Restarting a timer that is still open first banks
 * its open segment, so a double start can never swallow the time before it.
 *
 * @param {object|null} previous Existing timer under that id, if any.
 * @param {{worksheetId: ?string, taskId: string, taskName: string}} identity Who the timer is for.
 * @param {number} now Epoch ms.
 * @returns {object} The running timer.
 */
export const startLocalTimer = (previous, {worksheetId, taskId, taskName}, now) => {
    const base = previous ? closeOpenSegment(previous, now) : null;

    return {
        v: LOCAL_TIMER_VERSION,
        worksheetId,
        taskId,
        taskName,
        status: 'running',
        startedAt: now,
        totalElapsed: base?.totalElapsed || 0,
        breakElapsed: base?.breakElapsed || 0,
        segments: [...(base?.segments || []), {type: 'work', start: now}],
    };
};

/**
 * Pause a running timer (banks the open work segment).
 * @param {object|undefined} timer Local timer.
 * @param {number} now Epoch ms.
 * @returns {object|undefined} Paused timer, or the input when it was not running.
 */
export const pauseLocalTimer = (timer, now) => (
    timer && timer.status === 'running' ? {...closeOpenSegment(timer, now), status: 'paused'} : timer
);

/**
 * Resume a paused timer (opens a new work segment).
 * @param {object|undefined} timer Local timer.
 * @param {number} now Epoch ms.
 * @returns {object|undefined} Running timer, or the input when it was not paused.
 */
export const resumeLocalTimer = (timer, now) => (
    timer && timer.status === 'paused'
        ? {...timer, status: 'running', segments: [...timer.segments, {type: 'work', start: now}]}
        : timer
);

/**
 * Start a break from a running or paused timer (banks the work segment, opens a break one).
 * @param {object|undefined} timer Local timer.
 * @param {number} now Epoch ms.
 * @param {string} [reason] Why the break was taken.
 * @returns {object|undefined} Timer on break, or the input when it could not start one.
 */
export const startLocalBreak = (timer, now, reason = '') => {
    if (!timer || (timer.status !== 'running' && timer.status !== 'paused')) {
        return timer;
    }

    const closed = closeOpenSegment(timer, now);

    return {...closed, status: 'break', segments: [...closed.segments, {type: 'break', start: now, reason}]};
};

/**
 * End a break (banks the break segment, opens a work one).
 * @param {object|undefined} timer Local timer.
 * @param {number} now Epoch ms.
 * @returns {object|undefined} Running timer, or the input when it was not on break.
 */
export const endLocalBreak = (timer, now) => {
    if (!timer || timer.status !== 'break') {
        return timer;
    }

    const closed = closeOpenSegment(timer, now);

    return {...closed, status: 'running', segments: [...closed.segments, {type: 'work', start: now}]};
};

/**
 * Stop a timer (banks whatever segment is open).
 * @param {object|undefined} timer Local timer.
 * @param {number} now Epoch ms.
 * @returns {object|undefined} Idle timer.
 */
export const stopLocalTimer = (timer, now) => (
    timer ? {...closeOpenSegment(timer, now), status: 'idle'} : timer
);

/**
 * Bring a timer read from AsyncStorage up to the current model.
 *
 * A {@link LOCAL_TIMER_VERSION} timer is returned as is — its live value is
 * `now - open.start`, so a cold start hours later is already correct. A legacy
 * (tick-counting) running / break timer already carries its ticks inside the
 * counters, so keeping the old segment start would count that time twice; its
 * open segment is re-based to `now` instead (the counters are kept).
 *
 * @param {object} timer Persisted timer.
 * @param {number} now Epoch ms.
 * @returns {object} Timer in the current model.
 */
export const migrateLoadedTimer = (timer, now) => {
    if (!timer || timer.v === LOCAL_TIMER_VERSION) {
        return timer;
    }

    const migrated = {...timer, v: LOCAL_TIMER_VERSION, segments: [...(timer.segments || [])]};
    const open = openSegmentOf(migrated);

    if (open && (migrated.status === 'running' || migrated.status === 'break')) {
        migrated.segments[migrated.segments.length - 1] = {...open, start: now};
    }

    return migrated;
};

export default {
    LOCAL_TIMER_VERSION,
    closeOpenSegment,
    withLiveElapsed,
    startLocalTimer,
    pauseLocalTimer,
    resumeLocalTimer,
    startLocalBreak,
    endLocalBreak,
    stopLocalTimer,
    migrateLoadedTimer,
};
